import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, useNavigate, Link } from 'react-router-dom'
import LocalizedLink from '@/components/layout/LocalizedLink'
import { useLocale } from '@/hooks/useLocale'
import { useTranslation } from 'react-i18next'
import { BookOpen, FileText, ArrowLeft, User, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import Breadcrumb from '@/components/layout/Breadcrumb'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { libraryApi, type Book, type Topic } from '@/services/libraryApiService'
import { mockBooks } from '@/services/mockDataService'
import { getLocalizedValue } from '@sakeena/shared'

// PDF Viewer
import { pdfjs, Document, Page } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import { resolveAssetPath } from '@/utils/basePath'

// Configure PDF.js worker - use static file from public to avoid sendWithPromise null errors
pdfjs.GlobalWorkerOptions.workerSrc = resolveAssetPath('/pdf.worker.min.mjs')

// Helper to normalize book data (handle both camelCase and PascalCase)
const normalizeBook = (book: any): any => ({
  id: book.id || book.Id || '',
  title: book.title || book.Title || '',
  author: book.author || book.Author || '',
  description: book.description || book.Description || '',
  categoryId: book.categoryId || book.CategoryId || '',
  categoryName: book.categoryName || book.CategoryName || '',
  coverImageUrl: book.coverImageUrl || book.CoverImageUrl || book.imageUrl || book.ImageUrl || '',
  pdfUrl: book.pdfUrl || book.PdfUrl || '',
  order: book.order ?? book.Order ?? 0,
  topics: (book.topics || book.Topics || []).map((t: any) => ({
    id: t.id || t.Id || '',
    title: t.title || t.Title || '',
    content: t.content || t.Content || '',
    order: t.order ?? t.Order ?? 0,
  })),
})

export default function LibraryDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { t, i18n } = useTranslation()
  const { localizePath } = useLocale()
  const currentLang = i18n.language || 'ar'
  const navigate = useNavigate()
  
  const [book, setBook] = useState<any | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // PDF state
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [useIframe, setUseIframe] = useState(false)

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setPageNumber(1)
    setPdfError(null)
    setUseIframe(false)
  }

  const handlePdfError = (err: any) => {
    console.error('PDF load error:', err)
    setPdfError(t('public.library.pdfLoadError'))
    // Fallback to iframe after a short delay — عند التبديل نمسح رسالة الخطأ لأن المعاينة البديلة تعمل
    setTimeout(() => {
      setUseIframe(true)
      setPdfError(null)
    }, 1000)
  }

  const changePage = (offset: number) => {
    setPageNumber((prev) => {
      if (!numPages) return prev
      const next = prev + offset
      if (next < 1) return 1
      if (next > numPages) return numPages
      return next
    })
  }

  const zoomIn = () => setScale((s) => Math.min(s + 0.2, 2.5))
  const zoomOut = () => setScale((s) => Math.max(s - 0.2, 0.5))

  useEffect(() => {
    const fetchBook = async () => {
      if (!id) return

      try {
        setIsLoading(true)
        setError(null)
        const data = await libraryApi.getBook(id, currentLang)
        if (data) {
          setBook(normalizeBook(data))
          setPdfError(null)
        } else {
          throw new Error('Book not found')
        }
      } catch (err) {
        console.error('Failed to fetch book:', err)
        setError('فشل تحميل بيانات الكتاب')
        
        // Fallback to mock data
        const mockBook = mockBooks.find((b) => b.id.toString() === id)
        if (mockBook) {
          setBook(normalizeBook({
            id: mockBook.id.toString(),
            title: mockBook.title,
            author: mockBook.author,
            description: mockBook.description,
            categoryId: mockBook.category.toString(),
            categoryName: mockBook.categoryName,
            coverImageUrl: mockBook.coverImage,
            pdfUrl: mockBook.pdfUrl,
            order: 0,
            topics: [],
          }))
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchBook()
  }, [id, currentLang])

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('common.library'), to: '/library' },
    { label: getLocalizedValue(book?.title, currentLang) || t('public.library.detail.title', { id }) },
  ]

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>{t('common.library')} - سكينة</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={breadcrumbItems} className="mb-6" />
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        </div>
      </>
    )
  }

  if (error && !book) {
    return (
      <>
        <Helmet>
          <title>{t('common.library')} - سكينة</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={breadcrumbItems} className="mb-6" />
          <Card className="p-6 text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Button variant="secondary" onClick={() => navigate(localizePath('/library'))}>
              <ArrowLeft className="w-4 h-4 ml-2" />
              {t('public.library.backToIndex')}
            </Button>
          </Card>
        </div>
      </>
    )
  }

  if (!book) {
    return (
      <>
        <Helmet>
          <title>{t('common.library')} - سكينة</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={breadcrumbItems} className="mb-6" />
          <Card className="p-6 text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">{t('public.library.notFound')}</p>
            <Button variant="secondary" onClick={() => navigate(localizePath('/library'))}>
              <ArrowLeft className="w-4 h-4 ml-2" />
              {t('public.library.backToIndex')}
            </Button>
          </Card>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{getLocalizedValue(book.title, currentLang)} - {t('common.library')} - سكينة</title>
        <meta name="description" content={getLocalizedValue(book.description || book.title, currentLang)} />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        {/* Back Button */}
        <Button
          variant="secondary"
          onClick={() => navigate(localizePath('/library'))}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 ml-2" />
          {t('public.library.backToIndex')}
        </Button>

        {/* Book Header */}
        <Card className="p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            {/* Cover Image */}
            <div className="w-full md:w-48 h-64 md:h-72 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
              {book.coverImageUrl ? (
                <img
                  src={book.coverImageUrl}
                  alt={getLocalizedValue(book.title, currentLang)}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <BookOpen className="w-16 h-16 text-gray-400 dark:text-gray-500" />
              )}
            </div>

            {/* Book Info */}
            <div className="flex-1">
              {book.categoryName && (
                <span className="inline-block text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-3 py-1 rounded-full mb-4">
                  {getLocalizedValue(book.categoryName, currentLang)}
                </span>
              )}
              
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {getLocalizedValue(book.title, currentLang)}
              </h1>

              {book.author && (
                <div className="flex items-center gap-2 text-lg text-gray-700 dark:text-gray-300 mb-4">
                  <User className="w-5 h-5" />
                  <span>{t('public.library.authorLabel')}: {getLocalizedValue(book.author, currentLang)}</span>
                </div>
              )}

              {book.description && (
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg mb-6">
                  {getLocalizedValue(book.description, currentLang)}
                </p>
              )}

              {book.topics && book.topics.length > 0 && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <FileText className="w-5 h-5" />
                  <span>{t('public.library.topicCount', { count: book.topics.length })}</span>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* PDF Preview */}
        {book.pdfUrl && (
          <Card className="p-6 md:p-8 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('public.library.bookPreviewPdf')}
              </h2>
              <a
                href={book.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                {t('public.library.openInNewWindow')}
              </a>
            </div>

            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              {/* Zoom controls */}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={zoomOut}
                >
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-700 dark:text-gray-300 w-16 text-center">
                  {(scale * 100).toFixed(0)}%
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={zoomIn}
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
              </div>

              {/* Page controls */}
              {numPages && numPages > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => changePage(-1)}
                    disabled={pageNumber <= 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <span>
                    {pageNumber} / {numPages}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => changePage(1)}
                    disabled={pageNumber >= numPages}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* PDF Viewer */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-auto max-h-[80vh] bg-gray-50 dark:bg-gray-800">
              {useIframe ? (
                <iframe
                  src={`${book.pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                  className="w-full h-[80vh]"
                  title={`PDF Preview - ${getLocalizedValue(book.title, currentLang)}`}
                  style={{ border: 'none' }}
                />
              ) : (
                <div className="flex justify-center p-4">
                  <Document
                    key={`${book.id}-${currentLang}-${book.pdfUrl || ''}`}
                    file={{
                      url: book.pdfUrl,
                      httpHeaders: {
                        'Access-Control-Allow-Origin': '*',
                      },
                      withCredentials: false,
                    }}
                    onLoadSuccess={handleDocumentLoadSuccess}
                    onLoadError={handlePdfError}
                    loading={
                      <div className="flex flex-col items-center justify-center py-12">
                        <LoadingSpinner />
                        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">جاري تحميل PDF...</p>
                      </div>
                    }
                    error={
                      <div className="text-center p-8">
                        <BookOpen className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                        <p className="text-red-600 dark:text-red-400 mb-4">
                          {pdfError || 'حدث خطأ أثناء تحميل ملف PDF. جاري المحاولة بطريقة بديلة...'}
                        </p>
                        <Button
                          variant="primary"
                          onClick={() => setUseIframe(true)}
                        >
                          استخدام معاينة المتصفح
                        </Button>
                      </div>
                    }
                    options={{
                      cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
                      cMapPacked: true,
                      standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/',
                    }}
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={scale}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      className="shadow-lg"
                      loading={
                        <div className="flex items-center justify-center py-8">
                          <LoadingSpinner />
                        </div>
                      }
                    />
                  </Document>
                </div>
              )}
            </div>

            {pdfError && (
              <div className="mt-4 text-center">
                <p className="text-sm text-red-600 dark:text-red-400 mb-2">{pdfError}</p>
                <Button
                  variant="secondary"
                  onClick={() => window.open(book.pdfUrl, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 ml-2" />
                  فتح الكتاب في نافذة جديدة
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Topics List */}
        {book.topics && book.topics.length > 0 ? (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              مواضيع الكتاب
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {book.topics.map((topic: any) => (
                <LocalizedLink
                  key={topic.id}
                  to={`/library/book/${id}?topic=${topic.id}`}
                  className="block"
                >
                  <Card className="p-6 hover:shadow-lg transition-all cursor-pointer h-full">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                          {getLocalizedValue(topic.title, currentLang)}
                        </h3>
                        {topic.content && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                            {getLocalizedValue(topic.content, currentLang).replace(/<[^>]*>/g, '').substring(0, 150)}...
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                </LocalizedLink>
              ))}
            </div>
          </div>
        ) : (
          <Card className="p-8 text-center">
            <BookOpen className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              لا توجد مواضيع متاحة لهذا الكتاب حالياً
            </p>
          </Card>
        )}
      </div>
    </>
  )
}
